import { AxiosError, AxiosRequestConfig } from 'axios';
import { logHttpError } from './errorLogging/GlobalErrorLogger';

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  retryCondition?: (error: AxiosError) => boolean;
  onRetry?: (retryCount: number, error: AxiosError) => void;
}

export interface RetryableRequest extends AxiosRequestConfig {
  _retryCount?: number;
  _retryConfig?: RetryConfig;
}

/**
 * Default retry configuration
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 0,  // WYŁĄCZ wszystkie retry - nie ponawiaj żadnych requestów
  baseDelay: 1000, // 1 second
  maxDelay: 5000, // 5 seconds
  retryCondition: (error: AxiosError) => {
    // NIE PONAWIAJ ŻADNYCH REQUESTÓW - zawsze zwracaj false
    // Użyj GlobalErrorLogger do wyświetlenia błędu na ekranie
    logHttpError(error, 'RetryService');
    return false; // Nigdy nie ponawiaj
  }
};

/**
 * Service for handling request retries with exponential backoff
 */
export class RetryService {
  /**
   * Calculates delay for retry using exponential backoff with jitter
   */
  static calculateDelay(retryCount: number, config: RetryConfig): number {
    const exponentialDelay = config.baseDelay * Math.pow(2, retryCount - 1);
    
    // Add jitter to prevent thundering herd problem
    const jitter = Math.random() * 0.1 * exponentialDelay;
    
    const delay = Math.min(exponentialDelay + jitter, config.maxDelay);
    
    return Math.floor(delay);
  }

  /**
   * Determines if a request should be retried
   */
  static shouldRetry(error: AxiosError, config: RetryConfig): boolean {
    const request = error.config as RetryableRequest;
    const retryCount = request._retryCount || 0;

    // Check if we've exceeded max retries
    if (retryCount >= config.maxRetries) {
      return false;
    }

    // Check retry condition
    return config.retryCondition ? config.retryCondition(error) : true;
  }

  /**
   * Creates a delay promise for retry timing
   */
  static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Executes a request with retry logic
   */
  static async executeWithRetry<T>(
    requestFn: () => Promise<T>,
    error: AxiosError,
    config: RetryConfig = DEFAULT_RETRY_CONFIG
  ): Promise<T> {
    const request = error.config as RetryableRequest;
    const retryCount = (request._retryCount || 0) + 1;

    if (!this.shouldRetry(error, config)) {
      // DODAJ TO:
      console.log(`Not retrying request: ${error.response?.status} ${error.message}`);
      throw error;
    }

    // Update retry count
    request._retryCount = retryCount;
    request._retryConfig = config;

    // Calculate delay
    const delay = this.calculateDelay(retryCount, config);

    // DODAJ TO:
    console.warn(`HTTP ${error.response?.status} error - retrying (${retryCount}/${config.maxRetries}): ${error.config?.url}`);

    // Call onRetry callback if provided
    config.onRetry?.(retryCount, error);

    // Wait for delay
    await this.delay(delay);

    try {
      return await requestFn();
    } catch (retryError) {
      // If this retry also fails, recursively try again or throw
      if (retryError instanceof Error && 'isAxiosError' in retryError) {
        return this.executeWithRetry(requestFn, retryError as AxiosError, config);
      }
      throw retryError;
    }
  }

  /**
   * Creates a retry-enabled request function
   */
  static createRetryableRequest<T>(
    requestFn: () => Promise<T>,
    config: Partial<RetryConfig> = {}
  ): () => Promise<T> {
    const retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };

    return async () => {
      try {
        return await requestFn();
      } catch (error) {
        if (error instanceof Error && 'isAxiosError' in error) {
          return this.executeWithRetry(requestFn, error as AxiosError, retryConfig);
        }
        throw error;
      }
    };
  }

  /**
   * Gets retry information from a request
   */
  static getRetryInfo(request: RetryableRequest): {
    retryCount: number;
    maxRetries: number;
    hasRetryConfig: boolean;
  } {
    return {
      retryCount: request._retryCount || 0,
      maxRetries: request._retryConfig?.maxRetries || DEFAULT_RETRY_CONFIG.maxRetries,
      hasRetryConfig: !!request._retryConfig
    };
  }

  /**
   * Resets retry information on a request
   */
  static resetRetryInfo(request: RetryableRequest): void {
    delete request._retryCount;
    delete request._retryConfig;
  }

  /**
   * Creates a custom retry condition for authentication-related errors
   */
  static createAuthRetryCondition(): (error: AxiosError) => boolean {
    return (error: AxiosError) => {
      // Don't retry authentication errors - let auth interceptors handle them
      if (error.response?.status === 401 || error.response?.status === 403) {
        return false;
      }

      // Use default retry condition for other errors
      return DEFAULT_RETRY_CONFIG.retryCondition!(error);
    };
  }

  /**
   * Creates a retry configuration optimized for API requests
   */
  static createApiRetryConfig(overrides: Partial<RetryConfig> = {}): RetryConfig {
    return {
      ...DEFAULT_RETRY_CONFIG,
      retryCondition: this.createAuthRetryCondition(),
      onRetry: (retryCount, error) => {
        console.warn(`API request retry ${retryCount}: ${error.message}`);
        overrides.onRetry?.(retryCount, error);
      },
      ...overrides
    };
  }
}